import { createFileRoute, Link } from "@tanstack/react-router";
import { EyebrowHeading, Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { VideoEmbed } from "@/components/site/VideoEmbed";
import { ContactForm } from "@/components/site/ContactForm";
import { ArrowRight, Check } from "lucide-react";
import { primeServices } from "@/content/prime-services";
import { FitCheck } from "@/components/site/FitCheck";
import { KineticHeading } from "@/components/site/wow/KineticHeading";
import { TickerTape } from "@/components/site/wow/TickerTape";
import { Reveal, RevealStagger, RevealItem } from "@/components/site/wow/Reveal";
import { Magnetic } from "@/components/site/wow/MagneticButton";
import { TiltCard } from "@/components/site/wow/TiltCard";
import { LivePulseBadge } from "@/components/site/wow/LivePulseBadge";
import { PrimeManifesto } from "@/components/site/wow/PrimeManifesto";
import { GearStrip } from "@/components/site/wow/GearStrip";
import { GrainOverlay } from "@/components/site/wow/GrainOverlay";
import { RelatedLinksRail } from "@/components/site/aeo/RelatedLinksRail";
import { QuickAnswer } from "@/components/site/aeo/QuickAnswer";
import { AnswerFaq } from "@/components/site/aeo/AnswerFaq";
import { toFaqJsonLd } from "@/lib/aeo/answers";
import { caseItems, featuredCase } from "@/content/cases";
import { LiveCountUp } from "@/components/site/case/LiveCountUp";
import { RiskRemoval } from "@/components/site/RiskRemoval";
import { useScrollDepth } from "@/hooks/useScrollDepth";

const PRIME_FAQ_IDS = [
  "sv_hur_fungerar_fenny_prime",
  "sv_skillnaden_pulse_prime",
  "sv_vad_kostar_reklamfilm",
];

const SITE_ORIGIN = "https://fenny.se";
const PRIME_URL = `${SITE_ORIGIN}/prime`;
import { SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
import { aeoGeo } from "@/lib/seo/schema";
const PRIME_OG_IMAGE = SOCIAL_IMAGE_URL;

const primeBreadcrumbJsonLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Hem", item: `${SITE_ORIGIN}/` },
    { "@type": "ListItem", position: 2, name: "Tjänster", item: `${SITE_ORIGIN}/tjanster` },
    { "@type": "ListItem", position: 3, name: "Prime", item: PRIME_URL },
  ],
};

const primeServiceJsonLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "Fenny Prime",
  serviceType: "Skräddarsydda premiumproduktioner",
  provider: { "@type": "Organization", name: "Fenny", url: SITE_ORIGIN },
  areaServed: ["SE", "NO", "DK", "FI"],
  description:
    "Reklamfilm, dokumentär, TV & streaming, live, studio, regi & manus, post-produktion och podcast — paketerat som enskilda projekt med full förproduktion.",
  image: PRIME_OG_IMAGE,
  url: PRIME_URL,
  offers: {
    "@type": "Offer",
    priceCurrency: "SEK",
    price: "150000",
    availability: "https://schema.org/InStock",
    url: PRIME_URL,
  },
};

const primeItemListJsonLd = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  itemListElement: primeServices.map((s, i) => ({
    "@type": "ListItem",
    position: i + 1,
    url: `${SITE_ORIGIN}/prime/${s.slug}`,
    name: s.title,
  })),
};

export const Route = createFileRoute("/prime")({
  head: () => ({
    meta: [
      { title: "Prime — Större produktioner som kräver mer | Fenny" },
      {
        name: "description",
        content:
          "Fenny Prime för skräddarsydd filmproduktion: reklamfilm, dokumentär, live och streaming. Från manus och regi till färdig film.",
      },
      { property: "og:title", content: "Prime — Större produktioner | Fenny" },
      {
        property: "og:description",
        content:
          "Skräddarsydda premiumproduktioner: reklamfilm, dokumentär, streaming, live och post-produktion.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/prime" },
      { property: "og:image", content: PRIME_OG_IMAGE },
      { property: "og:image:alt", content: "Fenny Prime — större produktioner" },
      { property: "og:image:width", content: "1024" },
      { property: "og:image:height", content: "576" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "Prime — Större produktioner | Fenny" },
      { name: "twitter:description", content: "Skräddarsydda premiumproduktioner från Fenny." },
      { name: "twitter:image", content: PRIME_OG_IMAGE },
      { name: "twitter:image:alt", content: "Fenny Prime — större produktioner" },
    ],
    links: [
      { rel: "canonical", href: "https://fenny.se/prime" },
      { rel: "preconnect", href: "https://i.ytimg.com" },
      { rel: "preconnect", href: "https://www.youtube.com" },
    ],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/prime", name: "Prime — Större produktioner som kräver mer | Fenny", description: "Prime är Fennys premium-spår: reklamfilm, dokumentär, TV, streaming, live och post. Skräddarsytt med regissör, manus och full förproduktion."})) },
      { type: "application/ld+json", children: JSON.stringify(primeBreadcrumbJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(primeServiceJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(primeItemListJsonLd) },
      { type: "application/ld+json", children: JSON.stringify(toFaqJsonLd(PRIME_FAQ_IDS)) },
    ],
  }),

  component: PrimePage,
});

const services = primeServices.map((s) => ({
  slug: s.slug,
  title: s.title,
  body: s.intro,
}));

const process = [
  { n: "01", title: "Strategisk planering", body: "Vi lyssnar, förstår visionen och sätter ramar." },
  { n: "02", title: "Brief & offert", body: "Manus, lookboard, produktionsplan och offert med tydligt estimat." },
  { n: "03", title: "Förproduktion", body: "Scouting, casting, riggplanering, A/B-kamera, provspelningar, manus och regi. Allt ska sitta innan vi rullar." },
  { n: "04", title: "Inspelning", body: "Regissör, fotografer, ljudtekniker och ljussättare. Hela teamet på plats med det som behövs." },
  { n: "05", title: "Post & leverans", body: "Klipp, grading, ljuddesign, grafik och VFX. Revideringar tills det sitter. Leverans i önskade format." },
];

const primeCases = [featuredCase, ...caseItems.filter((c) => c.service === "Prime")].slice(0, 3);

function PrimePage() {
  useScrollDepth("prime");
  return (
    <div className="pt-28">
      {/* Hero — pure typography, no image */}
      <section className="relative isolate overflow-hidden pb-12 md:pb-20">
        <div
          aria-hidden="true"
          className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_at_top,color-mix(in_oklab,var(--butter)_8%,transparent)_0%,transparent_60%)]"
        />
        <GrainOverlay opacity={0.06} />
        <div className="container-wide">
          <Reveal>
            <LivePulseBadge tone="butter" className="mb-8">
              Prime · premium-spår
            </LivePulseBadge>
          </Reveal>
          <KineticHeading
            text="Större produktioner som kräver mer."
            className="text-5xl font-semibold leading-[0.98] text-foreground md:text-8xl lg:text-[112px] max-w-6xl"
          />
          <Reveal delay={0.7}>
            <div className="mt-10 flex flex-wrap items-baseline gap-3 text-2xl text-muted-foreground md:text-3xl">
              <span>Vi gör</span>
              <TickerTape
                words={["reklamfilm.", "dokumentär.", "TV & streaming.", "live.", "post-produktion.", "podcast."]}
                className="text-butter font-semibold"
              />
            </div>
          </Reveal>
          <Reveal delay={0.9}>
            <p className="mt-10 max-w-3xl text-pretty text-lg text-muted-foreground md:text-xl">
              När ditt varumärke kräver något större än standardlösningar levererar Prime. Vi samlar
              erfarna regissörer, kreatörer och tekniker för att skapa film som inte bara syns — utan
              stannar kvar i minnet.
            </p>
          </Reveal>
          <Reveal delay={0.95}>
            <QuickAnswer answerId="sv_hur_fungerar_fenny_prime" className="mt-8" />
          </Reveal>
          <Reveal delay={1.05}>
            <div className="mt-10 flex flex-wrap gap-3">
              <Magnetic>
                <Button to="/kontakt" variant="mint" arrow trackId="contact_us" trackProps={{ location: "prime_hero" }}>
                  Begär Prime-offert
                </Button>
              </Magnetic>
              <Button href="#tjanster" variant="outline">Se våra Prime-tjänster</Button>
              <Button href="#process" variant="ghost">Så funkar Prime</Button>
              <Button href="#case" variant="ghost">Prime in action</Button>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Feature reel — letterboxed cinema */}
      <Section className="py-10 md:py-14">
        <Reveal>
          <div className="mx-auto max-w-6xl rounded-3xl border border-white/10 bg-black p-2 md:p-3">
            <VideoEmbed id="9iiyNS4yNzA" title="Case — Maria på Garageportexperten" />
          </div>
        </Reveal>
      </Section>

      <GearStrip />

      {/* Manifesto */}
      <PrimeManifesto />

      {/* Intro */}
      <Section className="py-16 md:py-24">
        <Reveal>
          <EyebrowHeading
            eyebrow="Vision till verklighet"
            title={<>Varje stark idé förtjänar att lyftas från tanke till verklighet.</>}
            lede="Vi arbetar nära er för att förstå visionen bakom projektet — oavsett om målet är att bygga varumärke, driva försäljning eller skapa något som berör och stannar kvar. Prime hanterar allt från kreativ brief till slutleverans: manusarbete, scouting, casting, inspelning, klippning, grading, ljuddesign. Varje fas med dedikerad kompetens. Resultatet: film som tål att ses. Om och om igen."
          />
        </Reveal>
      </Section>

      <FitCheck
        eyebrow="Är Prime rätt för er?"
        title="Prime är inte allt vi gör — men det är de stora ögonblicken."
        fits={[
          "Ni planerar en kampanj, brandfilm, dokumentär eller live-produktion",
          "Visionen kräver regissör, manus och full förproduktion",
          "Ni vill samla allt — kreativt och tekniskt — hos ett team",
        ]}
        noFits={[
          "Ni behöver löpande månadsflöde — se Pulse",
          "Budget under 100 000 kr för enskild produktion (Pilot kan vara rätt)",
        ]}
        tone="butter"
      />

      {/* Prime-tjänster bento */}
      <Section id="tjanster" className="scroll-mt-28 bg-surface/40">
        <EyebrowHeading
          eyebrow="Prime-tjänster"
          title="Vad vi gör inom Prime"
          lede="Specialiserade discipliner — du kan boka en eller flera."
        />
        <RevealStagger className="mt-14 grid auto-rows-[minmax(220px,auto)] gap-5 md:grid-cols-3">
          {services.map((s, i) => {
            // First card spans 2 cols on desktop = bento accent
            const featured = i === 0;
            return (
              <RevealItem key={s.title} className={featured ? "md:col-span-2 md:row-span-2" : ""}>
                <Link
                  to="/prime/$slug"
                  params={{ slug: s.slug }}
                  className="group block h-full"
                >
                  <TiltCard
                    glow="butter"
                    intensity={featured ? 4 : 6}
                    className={`flex h-full flex-col rounded-3xl border bg-surface p-7 transition-colors ${
                      featured ? "border-butter/30 md:p-10" : "border-white/10 hover:border-butter/30"
                    }`}
                  >
                    <div className="mb-4 text-xs font-mono text-muted-foreground">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <h3 className={featured ? "text-3xl font-semibold leading-tight md:text-5xl" : "text-xl font-semibold leading-tight"}>
                      {s.title}
                    </h3>
                    <p className={`mt-3 flex-1 leading-relaxed text-muted-foreground ${featured ? "text-base md:text-lg" : "text-sm"}`}>
                      {s.body}
                    </p>
                    <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-butter transition-transform group-hover:translate-x-0.5">
                      Läs mer <ArrowRight className="h-4 w-4" />
                    </span>
                  </TiltCard>
                </Link>
              </RevealItem>
            );
          })}
        </RevealStagger>

        {/* Bröllop — tillfälligt dolt */}
      </Section>

      {/* Case spotlight */}
      <Section id="case" className="scroll-mt-28">
        <EyebrowHeading
          eyebrow="Prime in action"
          title="Tre projekt. Tre olika ambitionsnivåer."
          lede="Klicka in på ett case för full breakdown — utmaning, lösning och resultat."
        />
        <RevealStagger className="mt-14 grid gap-5 md:grid-cols-3">
          {primeCases.map((c) => (
            <RevealItem key={c.slug}>
              <Link to="/case/$slug" params={{ slug: c.slug }} className="group block h-full">
                <TiltCard
                  glow="butter"
                  className="flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 bg-surface transition-colors hover:border-butter/30"
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={`https://i.ytimg.com/vi/${c.id}/hqdefault.jpg`}
                      alt={c.title}
                      loading="lazy"
                      className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-surface via-transparent to-transparent" />
                  </div>
                  <div className="flex flex-1 flex-col p-6">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-butter">{c.client} · {c.year}</div>
                    <h3 className="mt-2 text-xl font-semibold leading-tight">{c.title}</h3>
                    <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">{c.excerpt}</p>
                    {c.metric && (
                      <div className="mt-5 border-t border-white/10 pt-4">
                        <div className="text-2xl font-semibold text-butter">{c.metric.value}</div>
                        <div className="text-xs text-muted-foreground">{c.metric.label}</div>
                      </div>
                    )}
                  </div>
                </TiltCard>
              </Link>
            </RevealItem>
          ))}
        </RevealStagger>
        <div className="mt-10">
          <Button to="/case" variant="outline" arrow>Se alla case</Button>
        </div>
      </Section>

      {/* Prime vs Pulse */}
      <Section id="vs-pulse" className="scroll-mt-28 bg-surface/40">
        <EyebrowHeading
          eyebrow="Prime vs Pulse"
          title="Många kunder kombinerar."
          lede="Pulse för det löpande flödet & Prime för de stora ögonblicken. Samma team. Samma projektledare. Samma faktura."
        />
        <div className="mt-12 grid gap-5 md:grid-cols-2">
          <ComparisonCard
            tag="Pulse"
            title="Löpande contentmaskineri"
            items={[
              "Fast månadsabonnemang",
              "2–6 klipp/månad",
              "Optimerat för sociala medier",
              "Från 14 995 kr/mån",
            ]}
            cta={{ label: "Utforska Pulse", to: "/pulse" }}
            tone="outline"
          />
          <ComparisonCard
            tag="Prime"
            title="Stora ögonblick"
            items={[
              "Skräddarsydd offert per projekt",
              "Reklamfilm, dokumentärer, TV & live",
              "Full förproduktion & casting",
              "Budgetar från 150 000 kr",
            ]}
            cta={{ label: "Begär Prime-offert", to: "/kontakt" }}
            tone="mint"
          />
        </div>
      </Section>

      {/* Process — vertical with stats */}
      <Section id="process" className="scroll-mt-28">
        <EyebrowHeading
          eyebrow="Så funkar Prime"
          title="Fem faser. Ingen blir överhoppad."
        />
        <div className="mt-14 grid gap-12 lg:grid-cols-[1fr_2fr] lg:gap-16">
          <Reveal className="lg:sticky lg:top-28 lg:self-start">
            <div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-butter">Resultat</div>
              <div className="mt-3 text-5xl font-semibold leading-none md:text-6xl">
                <LiveCountUp value={97} suffix="%" />
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                av Prime-produktioner levereras på utsatt premiärdatum.
              </p>
              <div className="mt-8 border-t border-white/10 pt-6">
                <div className="text-[11px] uppercase tracking-[0.18em] text-butter">Snittlängd</div>
                <div className="mt-2 text-3xl font-semibold">
                  <LiveCountUp value={6} suffix=" v" />
                </div>
                <p className="mt-2 text-sm text-muted-foreground">från brief till leverans.</p>
              </div>
            </div>
          </Reveal>
          <RevealStagger className="space-y-4">
            {process.map((p) => (
              <RevealItem key={p.n}>
                <div className="rounded-3xl border border-white/10 bg-surface p-7 transition-colors hover:border-butter/40">
                  <div className="flex items-baseline gap-5">
                    <span className="text-4xl font-semibold text-butter md:text-5xl">{p.n}</span>
                    <div>
                      <h3 className="text-xl font-semibold leading-tight md:text-2xl">{p.title}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-muted-foreground md:text-[15px]">{p.body}</p>
                    </div>
                  </div>
                </div>
              </RevealItem>
            ))}
          </RevealStagger>
        </div>
        <div className="mt-14 flex flex-wrap gap-3">
          <Magnetic>
            <Button to="/kontakt" variant="mint" arrow trackId="contact_us" trackProps={{ location: "prime_process" }}>Kontakta oss</Button>
          </Magnetic>
          <Button to="/work" variant="outline">Tidigare projekt</Button>
        </div>
      </Section>

      {/* Risk removal — Prime-specific reducers */}
      <RiskRemoval variant="prime" />


      <AnswerFaq
        answerIds={PRIME_FAQ_IDS}
        eyebrow="Frågor & svar"
        title="Vanliga frågor om Prime"
        sectionId="faq"
        className="pb-10 md:pb-12"
      />

      <Section className="py-6">
        <RelatedLinksRail sourcePath="/prime" />
      </Section>


      <ContactForm location="prime" defaultIntent="prime" />
    </div>
  );
}

function ComparisonCard({
  tag,
  title,
  items,
  cta,
  tone,
}: {
  tag: string;
  title: string;
  items: string[];
  cta: { label: string; to: string };
  tone: "mint" | "outline";
}) {
  return (
    <Reveal>
      <article
        className={
          "relative flex h-full flex-col rounded-3xl border bg-surface p-8 transition-colors " +
          (tone === "mint" ? "border-mint/40" : "border-white/10 hover:border-white/25")
        }
      >
        <div className="mb-3 text-xs uppercase tracking-widest text-muted-foreground">{tag}</div>
        <h3 className="text-3xl font-semibold leading-tight md:text-4xl">{title}</h3>
        <ul className="mt-6 space-y-3">
          {items.map((i) => (
            <li key={i} className="flex items-start gap-3 text-sm text-foreground/90">
              <Check className="mt-0.5 h-4 w-4 shrink-0 text-mint" />
              <span>{i}</span>
            </li>
          ))}
        </ul>
        <div className="mt-8">
          <Button to={cta.to} variant={tone === "mint" ? "mint" : "outline"} arrow>
            {cta.label}
          </Button>
        </div>
      </article>
    </Reveal>
  );
}
